import { useEffect, useState } from "react";
import { useLenis } from "lenis/react";
import { useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { RuotaMark } from "./RuotaMark";

const LINKS = [
  { label: "About", href: "#about", testid: "nav-link-about" },
  { label: "Our Approach", href: "#philosophy", testid: "nav-link-process" },
  { label: "Inspiration", href: "#destinations", testid: "nav-link-destinations" },
  { label: "Group Travel", href: "#group-travel", testid: "nav-link-group-travel" },
  { label: "Contact", href: "#enquiry", testid: "nav-link-contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const lenis = useLenis();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    if (document.querySelector(href)) {
      lenis?.scrollTo(href, { offset: -72, duration: 1.4 });
    } else {
      navigate("/", { state: { scrollTo: href } });
    }
  };

  const light = scrolled || open;

  return (
    <header
      data-testid="navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,color,box-shadow] duration-500 print:hidden ${
        light
          ? "bg-[rgba(247,240,227,0.85)] text-[#0D1F23] shadow-[0_1px_0_rgba(22,138,145,0.15)] backdrop-blur-md"
          : "bg-transparent text-[#F7F0E3]"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 sm:px-10 lg:px-16">
        <a
          href="#top"
          data-testid="nav-brand-logo"
          onClick={(e) => go(e, "#top")}
          className="group mr-10 flex items-center gap-3"
        >
          <RuotaMark size={34} duration={14} className="transition-transform duration-700 group-hover:scale-110" />
          <span className="flex flex-col items-stretch whitespace-nowrap font-display leading-none">
            <span className="text-5xl font-semibold tracking-[0.12em]">RUOTA</span>
            <span className="mt-2 flex w-full justify-between text-xl font-bold">
              {"TRAVEL".split("").map((l) => (
                <span key={l}>{l}</span>
              ))}
            </span>
          </span>
        </a>

        <nav className="hidden items-center gap-8 md:flex">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={l.testid}
              onClick={(e) => go(e, l.href)}
              className="font-accent text-[13px] font-medium uppercase tracking-[0.2em] opacity-90 transition-opacity duration-300 hover:opacity-100"
            >
              {l.label}
            </a>
          ))}
          <a
            href="#enquiry"
            data-testid="nav-cta-enquire"
            onClick={(e) => go(e, "#enquiry")}
            className="rounded-full bg-[#168A91] px-5 py-2.5 font-accent text-[11px] uppercase tracking-[0.2em] text-[#F7F0E3] transition-colors duration-300 hover:bg-[#116D73]"
          >
            Begin Here
          </a>
        </nav>

        <button
          data-testid="nav-mobile-menu-button"
          aria-label="Toggle menu"
          onClick={() => setOpen((o) => !o)}
          className="md:hidden"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div className="border-t border-[rgba(22,138,145,0.15)] bg-[rgba(247,240,227,0.95)] px-6 py-6 text-[#0D1F23] backdrop-blur-md md:hidden">
          <div className="flex flex-col gap-5">
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`${l.testid}-mobile`}
                onClick={(e) => go(e, l.href)}
                className="font-display text-2xl"
              >
                {l.label}
              </a>
            ))}
            <a
              href="#enquiry"
              data-testid="nav-cta-enquire-mobile"
              onClick={(e) => go(e, "#enquiry")}
              className="mt-2 w-max rounded-full bg-[#168A91] px-6 py-3 font-accent text-[11px] uppercase tracking-[0.2em] text-[#F7F0E3]"
            >
              Begin Here
            </a>
          </div>
        </div>
      )}
    </header>
  );
};
